// src/pages/static/PrivacyPolicy.tsx
//
// Privacy Policy for RCCG Continent 2 / GIMLP Mentorship Programme.
// Compliant with:
//   • Nigeria Data Protection Act 2023 (NDPA)
//   • UK GDPR (UK members)
//   • EU GDPR Regulation 2016/679 (EU members)
//   • General international data protection best practice
//
// Last reviewed: 21 March 2026
// Route: /privacy-policy

import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Shield, ArrowLeft, ExternalLink } from "lucide-react";

/* ─────────────────────────────────────────────────────────────────
   SHARED LAYOUT HELPERS
───────────────────────────────────────────────────────────────── */

const PageHero: React.FC<{ title: string; sub: string; badge: string }> = ({
  title,
  sub,
  badge,
}) => (
  <div className="bg-green-950 pt-24 pb-14 px-4 md:px-10 text-center">
    <div className="inline-flex items-center gap-2 border border-green-500/50 rounded-full px-4 py-1.5 text-green-400 text-[10px] font-black tracking-[0.22em] uppercase mb-5">
      <Shield size={11} />
      {badge}
    </div>
    <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight mb-4">
      {title}
    </h1>
    <p className="text-white/55 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
      {sub}
    </p>
  </div>
);

const Section: React.FC<{
  id: string;
  title: string;
  children: React.ReactNode;
}> = ({ id, title, children }) => (
  <section id={id} className="mb-10">
    <h2 className="text-lg sm:text-xl font-black text-gray-900 mb-4 pb-2 border-b border-gray-100 tracking-wide">
      {title}
    </h2>
    <div className="space-y-4 text-gray-600 leading-relaxed text-sm md:text-base">
      {children}
    </div>
  </section>
);

const SubSection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="mt-5">
    <h3 className="text-sm font-black text-gray-800 mb-2 uppercase tracking-widest">
      {title}
    </h3>
    <div className="space-y-3 text-sm md:text-base">{children}</div>
  </div>
);

const Ul: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="list-none space-y-2 pl-4">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2">
        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-green-600 shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const TOC_ITEMS = [
  { id: "who-we-are", label: "1. Who We Are" },
  { id: "data-collected", label: "2. Data We Collect" },
  { id: "legal-basis", label: "3. Legal Basis for Processing" },
  { id: "how-we-use", label: "4. How We Use Your Data" },
  { id: "sharing", label: "5. Sharing Your Data" },
  { id: "international", label: "6. International Transfers" },
  { id: "retention", label: "7. How Long We Keep Your Data" },
  { id: "your-rights", label: "8. Your Rights" },
  { id: "security", label: "9. Data Security" },
  { id: "cookies", label: "10. Cookies" },
  { id: "children", label: "11. Children's Privacy" },
  { id: "changes", label: "12. Changes to This Policy" },
  { id: "contact-us", label: "13. Contact Us" },
];

/* ─────────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────────── */

const PrivacyPolicy: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <PageHero
        badge="Data Privacy"
        title="Privacy Policy"
        sub="We are committed to handling your personal information with integrity, transparency, and care — in line with the Nigeria Data Protection Act 2023, UK GDPR, EU GDPR, and international best practice."
      />

      {/* Effective date banner */}
      <div className="bg-green-50 border-b border-green-100 px-4 md:px-10 py-3 text-center">
        <p className="text-xs text-green-700 font-semibold">
          Effective date: 21 March 2026 &nbsp;·&nbsp; Last updated: 21 March
          2026
        </p>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-10 py-12 grid lg:grid-cols-[280px_1fr] gap-10 items-start">
        {/* ── Sidebar TOC (sticky on desktop) ── */}
        <aside className="hidden lg:block sticky top-24">
          <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-green-600 mb-3">
              Contents
            </p>
            <nav className="space-y-1">
              {TOC_ITEMS.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className="block text-xs text-gray-500 hover:text-green-700 hover:font-semibold py-1 transition-colors no-underline"
                >
                  {item.label}
                </a>
              ))}
            </nav>
            <div className="mt-5 pt-4 border-t border-gray-200">
              <Link
                to="/data-protection"
                className="inline-flex items-center gap-1.5 text-xs text-green-600 hover:text-green-700 font-semibold no-underline"
              >
                <ExternalLink size={11} />
                Data Protection Policy
              </Link>
            </div>
          </div>
        </aside>

        {/* ── Main content ── */}
        <main>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-green-700 font-semibold mb-8 no-underline transition-colors"
          >
            <ArrowLeft size={13} /> Back to home
          </Link>

          {/* 1 */}
          <Section id="who-we-are" title="1. Who We Are">
            <p>
              This Privacy Policy applies to the{" "}
              <strong>GIMLP Mentorship Programme</strong> operated by
              <strong> RCCG Continent 2</strong> (referred to in this policy as
              "we", "us", or "our"). RCCG Continent 2 is a regional arm of the
              Redeemed Christian Church of God (RCCG) covering regions across
              the United Kingdom, Europe, and Africa, with its continental
              headquarters in London, United Kingdom.
            </p>
            <p>
              The GIMLP portal (the "Platform") is a digital mentorship and
              leadership development platform accessible at our website. We act
              as the <strong>data controller</strong> in respect of personal
              data collected through this Platform.
            </p>
            <p>
              For the purpose of the{" "}
              <strong>Nigeria Data Protection Act 2023 (NDPA)</strong>, we are a
              data controller registered with the Nigeria Data Protection
              Commission (NDPC). For the purpose of <strong>UK GDPR</strong> and{" "}
              <strong>EU GDPR</strong>, we are a data controller responsible for
              your personal data.
            </p>
          </Section>

          {/* 2 */}
          <Section id="data-collected" title="2. Data We Collect">
            <p>We collect personal data in the following circumstances:</p>

            <SubSection title="a) Account Registration (Programme Members)">
              <Ul
                items={[
                  "Full name (first name and last name)",
                  "Email address",
                  "Password (stored in hashed, non-reversible form — we never store plain-text passwords)",
                  "RCCG region and province affiliation",
                  "Ministry role (e.g. youth leader, pastor, coordinator)",
                  "Programme start quarter",
                  "Membership tier information",
                ]}
              />
            </SubSection>

            <SubSection title="b) Event Registration (Public)">
              <Ul
                items={[
                  "First name and last name",
                  "Email address",
                  "Phone number (optional — used only for event reminders)",
                  "RCCG Continent 2 region",
                  "Privacy consent timestamp and confirmation code",
                ]}
              />
            </SubSection>

            <SubSection title="c) Programme Participation">
              <Ul
                items={[
                  "Task submissions and written responses",
                  "Content engagement records (videos watched, documents accessed)",
                  "Video progress and written summaries submitted as evidence of engagement",
                  "Community discussion posts and replies",
                  "Journey activity logs (what actions you take on the platform and when)",
                  "Assessment scores and grading records",
                ]}
              />
            </SubSection>

            <SubSection title="d) Technical & Usage Data">
              <Ul
                items={[
                  "Browser type and version",
                  "IP address (not stored beyond server logs, not used to identify individuals)",
                  "Pages visited and time spent on the platform",
                  "Device type and operating system",
                ]}
              />
            </SubSection>

            <p className="mt-4 text-sm bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-green-800">
              <strong>We do not collect</strong> sensitive personal data such as
              racial or ethnic origin, religious beliefs, political opinions,
              health data, biometric data, or financial account details through
              this Platform.
            </p>
          </Section>

          {/* 3 */}
          <Section id="legal-basis" title="3. Legal Basis for Processing">
            <p>
              We process your personal data on the following legal grounds, in
              accordance with the NDPA 2023, UK GDPR Article 6, and EU GDPR
              Article 6:
            </p>
            <Ul
              items={[
                "Consent — where you have given clear, explicit consent before we process your data (e.g. by accepting this policy when registering for an event or joining the programme). You may withdraw consent at any time.",
                "Contractual necessity — where processing is necessary to provide you with the services you have requested (e.g. managing your programme membership, tracking your task submissions, and recording your progress).",
                "Legitimate interests — where we have a legitimate interest in processing your data that is not overridden by your rights (e.g. improving the platform, communicating operational updates relevant to your participation, and maintaining programme integrity).",
                "Legal obligation — where we are required to process data to comply with applicable law (e.g. record-keeping obligations, responding to lawful requests from authorities).",
              ]}
            />
            <p>
              Where we rely on <strong>consent</strong> as the legal basis, you
              have the right to withdraw that consent at any time by contacting
              us using the details in Section 13. Withdrawal of consent does not
              affect the lawfulness of processing carried out before the
              withdrawal.
            </p>
          </Section>

          {/* 4 */}
          <Section id="how-we-use" title="4. How We Use Your Data">
            <p>We use your personal data to:</p>
            <Ul
              items={[
                "Create and manage your account and programme membership",
                "Deliver the mentorship programme — including content, tasks, assessments, and progress tracking",
                "Record event registrations and issue confirmation codes",
                "Send programme-related communications such as quarter updates, task reminders, and announcements",
                "Enable community features such as discussion boards and regional communications",
                "Administer Legacy Fellows nominations and eligibility assessments",
                "Generate anonymised, aggregated engagement reports for programme administration and M&E (monitoring and evaluation)",
                "Improve the platform through analysis of usage patterns (on an aggregated, non-identifiable basis)",
                "Prevent fraud, misuse, and unauthorised access to the platform",
                "Comply with our legal obligations",
              ]}
            />
            <p>
              We do <strong>not</strong> use your data for automated
              decision-making or profiling that produces legal or similarly
              significant effects. We do <strong>not</strong> use your data for
              advertising or marketing purposes unrelated to the GIMLP
              programme.
            </p>
          </Section>

          {/* 5 */}
          <Section id="sharing" title="5. Sharing Your Data">
            <p>
              We do not sell, rent, or trade your personal data to third
              parties. We may share your data only in the following limited
              circumstances:
            </p>

            <SubSection title="a) Within RCCG Continent 2">
              <p>
                Authorised administrators, regional coordinators, and programme
                leaders within RCCG Continent 2 may access your data on a
                need-to-know basis for the purpose of managing and delivering
                the mentorship programme. Access is role-based and logged.
              </p>
            </SubSection>

            <SubSection title="b) Service Providers">
              <p>
                We use carefully selected third-party service providers to
                operate the platform, including cloud hosting, database
                services, and email delivery. These providers act as data
                processors on our behalf and are bound by data processing
                agreements that require them to protect your data and use it
                only for the purposes we specify. They do not have permission to
                use your data for their own purposes.
              </p>
            </SubSection>

            <SubSection title="c) Legal Requirements">
              <p>
                We may disclose your data if required to do so by law, court
                order, or in response to a valid request from a regulatory
                authority (e.g. the Nigeria Data Protection Commission, UK
                Information Commissioner's Office, or equivalent).
              </p>
            </SubSection>

            <SubSection title="d) With Your Consent">
              <p>
                We will share your data for any other purpose only with your
                explicit prior consent.
              </p>
            </SubSection>
          </Section>

          {/* 6 */}
          <Section id="international" title="6. International Transfers">
            <p>
              RCCG Continent 2 operates across multiple countries, and our
              members are located in the United Kingdom, Nigeria, and other
              countries across Europe, Africa, and beyond. Your personal data
              may be transferred to and processed in countries outside your
              country of residence, including Nigeria and the United Kingdom.
            </p>
            <p>
              Where we transfer personal data internationally, we ensure
              appropriate safeguards are in place in accordance with applicable
              law:
            </p>
            <Ul
              items={[
                "For transfers from the UK: we rely on UK adequacy regulations or appropriate safeguards such as the UK International Data Transfer Agreement (IDTA) or Standard Contractual Clauses (SCCs).",
                "For transfers from the EU: we rely on European Commission adequacy decisions or EU Standard Contractual Clauses (SCCs) where applicable.",
                "For transfers involving Nigerian residents: we comply with the NDPA 2023 requirements for cross-border data transfers, including ensuring the recipient country provides adequate protection or that appropriate safeguards are in place.",
              ]}
            />
            <p>
              By using the Platform, you acknowledge that your data may be
              transferred internationally in accordance with this policy and the
              safeguards described above.
            </p>
          </Section>

          {/* 7 */}
          <Section id="retention" title="7. How Long We Keep Your Data">
            <p>
              We retain your personal data only for as long as necessary for the
              purposes for which it was collected, or as required by applicable
              law. Our retention principles are:
            </p>
            <Ul
              items={[
                "Active programme members: data is retained for the duration of your programme membership and for 3 years following the end of active participation, to support continuity if you re-engage with the programme.",
                "Event registrations: registration records are retained for 2 years following the event date for administrative, reporting, and audit purposes.",
                "Task submissions and engagement records: retained for 5 years to support legacy reporting, M&E assessments, and any future fellowship or nomination processes.",
                "Inactive accounts: accounts with no login activity for 24 consecutive months may be deactivated and subject to data deletion, subject to prior notification.",
                "Legal obligations: where we are required by law to retain data for a longer period (e.g. financial records, regulatory compliance), we will retain it for the legally required period.",
              ]}
            />
            <p>
              When data is no longer required, it is securely deleted or
              anonymised so that it can no longer be linked to you.
            </p>
          </Section>

          {/* 8 */}
          <Section id="your-rights" title="8. Your Rights">
            <p>
              Depending on your location, you have the following rights in
              relation to your personal data. We will respond to verified
              requests within <strong>30 days</strong> (or within the period
              required by applicable law).
            </p>

            <SubSection title="Rights under the Nigeria Data Protection Act 2023">
              <Ul
                items={[
                  "Right of access — to obtain a copy of the personal data we hold about you",
                  "Right to rectification — to have inaccurate or incomplete data corrected",
                  "Right to erasure — to request deletion of your data where it is no longer necessary or where consent is withdrawn",
                  "Right to object — to object to processing of your data for certain purposes",
                  "Right to restrict processing — to request that we temporarily stop processing your data in certain circumstances",
                  "Right to data portability — to receive your data in a structured, machine-readable format",
                  "Right to withdraw consent — at any time where processing is based on consent",
                ]}
              />
            </SubSection>

            <SubSection title="Additional rights under UK GDPR / EU GDPR (for UK and EU-based members)">
              <Ul
                items={[
                  "All rights listed above under the NDPA 2023 apply equally",
                  "Right not to be subject to automated decision-making — where a decision is made solely by automated means and produces significant effects",
                  "Right to lodge a complaint with the relevant supervisory authority (UK: Information Commissioner's Office at ico.org.uk; EU: your national data protection authority)",
                ]}
              />
            </SubSection>

            <p className="mt-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-amber-800 text-sm">
              To exercise any of your rights, please contact us at the address
              in Section 13. We may need to verify your identity before
              processing your request. We will never charge you for exercising
              your rights.
            </p>
          </Section>

          {/* 9 */}
          <Section id="security" title="9. Data Security">
            <p>
              We take the security of your personal data seriously and implement
              appropriate technical and organisational measures to protect it
              against accidental loss, unauthorised access, disclosure,
              alteration, or destruction. Our security measures include:
            </p>
            <Ul
              items={[
                "Passwords are stored using strong one-way cryptographic hashing (bcrypt) — we never store or transmit plain-text passwords",
                "All data transmissions are encrypted in transit using TLS (Transport Layer Security)",
                "Access to personal data is restricted to authorised personnel on a role-based, need-to-know basis",
                "Authentication tokens are short-lived and automatically expire",
                "Database access is restricted by IP allowlist and requires multi-factor authentication for administrative access",
                "Regular security reviews and access audits",
                "Incident response procedures are in place to detect, contain, and report data breaches where required by law",
              ]}
            />
            <p>
              While we take every reasonable precaution, no method of
              transmission over the internet or electronic storage is completely
              secure. We cannot guarantee absolute security. If you believe your
              account has been compromised, please contact us immediately.
            </p>
            <p>
              In the event of a personal data breach that is likely to result in
              a risk to your rights and freedoms, we will notify the relevant
              supervisory authority and, where required, affected individuals
              within the timeframes prescribed by applicable law (typically 72
              hours under GDPR, and as required under the NDPA 2023).
            </p>
          </Section>

          {/* 10 */}
          <Section id="cookies" title="10. Cookies">
            <p>
              The Platform uses essential cookies and similar technologies
              necessary for the platform to function. These include:
            </p>
            <Ul
              items={[
                "Authentication tokens — to keep you logged in during your session",
                "Session state — to remember your preferences and maintain your progress within a session",
                "Security cookies — to detect and prevent fraudulent activity",
              ]}
            />
            <p>
              We do not currently use tracking, advertising, or analytics
              cookies that monitor your behaviour across other websites. If we
              introduce such cookies in the future, we will update this policy
              and obtain your consent where required by law.
            </p>
          </Section>

          {/* 11 */}
          <Section id="children" title="11. Children's Privacy">
            <p>
              The GIMLP Mentorship Programme is designed for youth and young
              adult leaders. While the programme may serve members aged 16 and
              above, we do not knowingly collect personal data from children
              under the age of 13 without verified parental or guardian consent.
            </p>
            <p>
              If you are a parent or guardian and believe that your child under
              13 has provided us with personal data without your consent, please
              contact us immediately using the details in Section 13 and we will
              take steps to delete such information.
            </p>
            <p>
              For members aged 13–15, we recommend that a parent or guardian
              reviews this policy with them. Parental consent may be required
              depending on the jurisdiction.
            </p>
          </Section>

          {/* 12 */}
          <Section id="changes" title="12. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time to reflect
              changes in our practices, legal obligations, or the services we
              provide. When we make material changes, we will:
            </p>
            <Ul
              items={[
                "Update the 'Last updated' date at the top of this page",
                "Notify registered members via email or an in-platform notification",
                "Where required by law, seek your consent before the changes take effect",
              ]}
            />
            <p>
              We encourage you to review this policy periodically. Your
              continued use of the Platform following notification of changes
              constitutes your acknowledgement of the updated policy.
            </p>
          </Section>

          {/* 13 */}
          <Section id="contact-us" title="13. Contact Us">
            <p>
              If you have any questions, concerns, or requests relating to this
              Privacy Policy or your personal data, please contact our Data
              Protection contact using the details below:
            </p>
            <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mt-4">
              <p className="font-black text-gray-900 mb-3 text-sm uppercase tracking-widest">
                Data Controller
              </p>
              <div className="space-y-1.5 text-sm text-gray-700">
                <p>
                  <strong>Organisation:</strong> RCCG Continent 2 — GIMLP
                  Mentorship Programme
                </p>
                <p>
                  <strong>Email:</strong>{" "}
                  <a
                    href="mailto:hello@rccgcont2lp.org"
                    className="text-green-600 hover:text-green-700 no-underline"
                  >
                    hello@rccgcont2lp.org
                  </a>
                </p>
                <p>
                  <strong>Website:</strong>{" "}
                  <a
                    href="https://rccgcont2lp.org"
                    target="_blank"
                    rel="noreferrer"
                    className="text-green-600 hover:text-green-700 no-underline"
                  >
                    rccgcont2lp.org
                  </a>
                </p>
              </div>
            </div>
            <p className="mt-5 text-sm">
              You also have the right to lodge a complaint with the relevant
              data protection supervisory authority in your country. For UK
              residents, this is the{" "}
              <a
                href="https://ico.org.uk"
                target="_blank"
                rel="noreferrer"
                className="text-green-600 hover:text-green-700 font-semibold no-underline"
              >
                Information Commissioner's Office (ICO)
              </a>
              . For Nigerian residents, this is the{" "}
              <a
                href="https://ndpc.gov.ng"
                target="_blank"
                rel="noreferrer"
                className="text-green-600 hover:text-green-700 font-semibold no-underline"
              >
                Nigeria Data Protection Commission (NDPC)
              </a>
              . For EU residents, please contact your national data protection
              authority.
            </p>
          </Section>

          {/* Related policies */}
          <div className="mt-10 pt-8 border-t border-gray-100 flex flex-col sm:flex-row gap-3">
            <Link
              to="/data-protection"
              className="inline-flex items-center gap-2 px-5 py-3 bg-green-600 hover:bg-green-500 text-white font-black text-xs uppercase tracking-widest rounded transition-all no-underline"
            >
              <ExternalLink size={12} />
              Data Protection Policy
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-3 border border-gray-200 hover:border-green-300 text-gray-600 hover:text-green-700 font-semibold text-xs uppercase tracking-widest rounded transition-all no-underline"
            >
              <ArrowLeft size={12} />
              Back to Home
            </Link>
          </div>
        </main>
      </div>

      {/* Footer */}
      <footer className="bg-green-950 px-4 md:px-10 py-7 flex flex-wrap items-center justify-between gap-4 mt-8">
        <p className="text-white/35 text-sm">
          GIMLP © {new Date().getFullYear()}. RCCG Continent 2. All rights
          reserved.
        </p>
        <div className="flex gap-6">
          <Link
            to="/privacy-policy"
            className="text-white/55 text-sm hover:text-white transition-colors no-underline font-semibold"
          >
            Privacy Policy
          </Link>
          <Link
            to="/data-protection"
            className="text-white/35 text-sm hover:text-white transition-colors no-underline"
          >
            Data Protection
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
